import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import DeepenUnderstandingImage from './DeepenUnderstandingImage'

const copy = {
  title: `Deepen understanding with a 360° view of your customers`,
  description: `With comprehensive customer insights, create personalized campaigns that are cohesive and actionable in real time.`,
  links: [
    { text: `Self-serve analytics`, href: `self-serve-analytics` },

    { text: `Data science modeling & advanced analytics`, href: `data-science-modeling` },
  ],
  elements: [
    {
      title: `Unify fragmented customer data`,
      description: `Convert disjointed data into actionable insights and precise predictive analytics enabling personalized customer experiences and informed business strategies.`,
    },
    {
      title: `Take control of your data and act on real-time insights`,
      description: `Implement real-time data governance and validation with Segment, establishing a trustworthy foundation for generating actionable insights and sophisticated modeling.`,
    },
    {
      title: `Measure ROI and improve experimentation`,
      description: `Measure the ROI of your engagement strategies accurately with Segment, from marketing campaigns to product investments, all while enhancing campaign effectiveness and customer satisfaction.`,
    },
  ],
}

const DeepenUnderstanding = () => {
  return (
    <Wrapper>
      <StyledSectionBaseTitle variant="noCopy">
        <Section.Contents>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <LinksWrapper>
              {copy.links.map((link, i) => (
                <StyledTypography variant="pLarge" key={link.text}>
                  <CustomDivider>{i === 0 ? null : `|`}</CustomDivider>
                  <a
                    href={`#${link.href}`}
                    onClick={(e) => {
                      e.preventDefault()
                      scrollToAnchor(link.href)
                    }}
                  >
                    {link.text}
                  </a>
                </StyledTypography>
              ))}
            </LinksWrapper>
          </CopyTextBase>
        </Section.Contents>
      </StyledSectionBaseTitle>
      <StyledSectionBaseContent variant="2Col">
        <Section.Contents>
          <DeepenUnderstandingImage />
        </Section.Contents>
        <Section.Copy alignSelf="center">
          <ColumnTrack>
            {copy.elements.map(({ title, description }, key) => (
              <CopyTextBase key={key}>
                <CopyText.Heading tag="h3" variant="pLargeBold">
                  {title}
                </CopyText.Heading>
                <CopyText.Description tag="p" variant="pMedium">
                  {description}
                </CopyText.Description>
              </CopyTextBase>
            ))}
          </ColumnTrack>
        </Section.Copy>
      </StyledSectionBaseContent>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.mint[10]};
`

const StyledSectionBaseTitle = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 0;
  }
`

const StyledSectionBaseContent = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 60px;
    padding-bottom: 50px;
  }
`

const LinksWrapper = styled.div`
  display: inline-flex;
  gap: 12px;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`

const StyledTypography = styled(Typography)`
  display: inline-flex;
  gap: 12px;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: 0;
  }
`

const CustomDivider = styled.span`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    display: none;
  }
`
export default DeepenUnderstanding
